import type { PropType } from 'vue';
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onUnmounted,
  ref,
  watch
} from 'vue';
import MkLoading from '@components/mk-loading';
import MkNoStatus from '@components/mk-no-status';
import styles from './index.module.scss';
import { handleAuth, showAuthMask } from '@/utils/auth';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'MkLayout',
  props: {
    // 接口请求中
    loading: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    // 素材状态
    status: {
      // 素材状态 0:草稿 1:已发布 2:已下架 3:待发布
      type: Number,
      default: 1
    },

    // 提示
    errMsg: { type: String },

    // 素材详情
    materialData: {
      type: Object,
      default: () => ({})
    },

    // 是否显示发送按钮
    isShowSendBtn: {
      type: Boolean,
      default: true
    },
    // 素材是否已删除
    isDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ['authSuccess'],
  setup(props, { slots }) {
    const appContext = getCurrentInstance()?.appContext as any;
    const globalProperties = appContext?.config?.globalProperties as Record<string, any>;

    const wH = ref(0);
    // 获取视窗高度
    const getClientHeight = () => {
      let clientHeight = 0;
      if (document.body.clientHeight && document.documentElement.clientHeight) clientHeight =
          document.body.clientHeight < document.documentElement.clientHeight
            ? document.body.clientHeight
            : document.documentElement.clientHeight;
      else clientHeight =
          document.body.clientHeight > document.documentElement.clientHeight
            ? document.body.clientHeight
            : document.documentElement.clientHeight;

      wH.value = clientHeight;
    };
    // 处理iphone底部前进返回按钮问题
    window.addEventListener('resize', getClientHeight);
    onUnmounted(() => {
      window.removeEventListener('resize', getClientHeight);
    });
    watch(
      () => props.materialData,
      (newVal) => {
        try {
          // 分享 title 优先级更高
          const title = newVal?.shareConfig?.title || newVal?.title;
          document.title = title || '';
        } catch (error) {
          // do nothing
        }
      }
    );
    getClientHeight();
    const mkReturnBarRef = ref(null);
    const mkFooterSendRef = ref(null);
    const mkFooterShareRef = ref(null);
    const mainH = computed(() => {
      let sH = 0;
      if (mkReturnBarRef.value) sH += 36;

      if (mkFooterSendRef.value) sH += 68;

      if (mkFooterShareRef.value) sH += 68;

      if (globalProperties.$isSendBtn || globalProperties.$isShareBtn) return `height: ${wH.value - sH}px;`;

      return `height: calc(100% - ${sH}px)`;
    });

    // 判断是否需要登录
    const showMask = ref(false);

    const initShowMask = async () => {
      showMask.value = await showAuthMask();
    };
    initShowMask();

    const showShareTip = ref(false);
    const route = useRoute();
    if (route.query?.mallShareTipMask === 'true' || window.localStorage.getItem('mallShareTipMask')) showShareTip.value = true;
     // 只要是分享类型都展示

    // 生成html
    const setHtml = () => {
      // 请求接口中
      if (props.loading) return <MkLoading />;

      if (props.isDelete) {
        const errMsg = '该素材已删除';
        return <MkNoStatus description={errMsg} />;
      }

      // 素材获取出错
      if (![3, 1].includes(props.status)) {
        let errMsg = props.errMsg;
        if (props.status === 2) errMsg = '该素材已下架';

        if (props.status === 0) errMsg = '该素材是草稿';

        if (props.status === 998) errMsg = '小程序中页面iframe不支持引用非业务域，请重新配置';

        if (props.status === 997) errMsg = '小程序中暂不支持访问';

        return <MkNoStatus description={errMsg} />;
      }

      const handleClick = () => {
        handleAuth(globalProperties);
      };

      const tipCLick = () => {
        window.localStorage.removeItem('mallShareTipMask');
        showShareTip.value = false;
      };

      // 授权蒙层
      const authModal = () => {
        if (!showMask.value) return '';
        return (
          <div id="auth-mask" class={styles.mask} onClick={handleClick}>
            <div class={styles.content}>
              <div class={styles.title}>点击查看更多，访问完整内容</div>
              <div class={styles.tip}>点击查看更多将会申请获取您的头像和昵称</div>
              <div class={styles.button}>查看更多</div>
            </div>
          </div>
        );
      };
      // 分享蒙层
      const shareTipModal = () => {
        return showShareTip.value ? (
          <div id="tip-mask" class={styles['share-tip-mask']} onClick={tipCLick}>
            <img
              src="https://cf-cdn.dustess.com/image-host/16/shareTimeLineCover.png"
              class={styles['share-mask-img']}
            />
          </div>
        ) : (
          ''
        );
      };

      // 有授权蒙层 禁止滚动
      function preventScroll(e: any) {
        e.preventDefault();
        e.stopPropagation();
      }
      nextTick(() => {
        const mask = document.getElementById('auth-mask');
        mask?.addEventListener('touchmove', preventScroll, { passive: false });
        mask?.addEventListener('wheel', preventScroll, { passive: false });
      });

      onUnmounted(() => {
        const mask = document.getElementById('auth-mask');
        mask?.removeEventListener('touchmove', preventScroll);
        mask?.removeEventListener('wheel', preventScroll);
      });

      return (
        <>
          <div class={[styles['mk-layout'], 'mk-layout']}>
            {/* 商城分享提示 */}
            {shareTipModal()}
            {/* 授权蒙层 */}
            {authModal()}
            {globalProperties.$urlObj.showGoBackBtn === 'true' ? (
              <mk-return-bar ref={mkReturnBarRef} />
            ) : (
              ''
            )}
            <div
              class={[
                styles['mk-layout-main'],
                `mk-layout-main ${
                  globalProperties.$isSendBtn || globalProperties.$isShareBtn
                    ? styles['mk-layout-main_scroll']
                    : ''
                }`
              ]}
              style={`${mainH.value}`}
            >
              {slots.default && slots.default()}
            </div>
            {/* 显示发送按钮：侧边栏 && 支持分享 */}
            {globalProperties.$isSendBtn && props.isShowSendBtn ? (
              <mk-footer-send ref={mkFooterSendRef} item={props.materialData} />
            ) : (
              ''
            )}

            {/* 分享按钮: 非侧边栏 && 在企微环境 && 手机中 */}
            {globalProperties.$isShareBtn && props.isShowSendBtn ? (
              <mk-footer-share ref={mkFooterShareRef} />
            ) : (
              ''
            )}
          </div>
        </>
      );
    };
    return () => setHtml();
  }
});
